import { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from "react";
import config from "../config";

const api = config.API_BASE_URL;

const formatTime = (ts) => {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDay = (ts) => {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

const REACTIONS = [
  { emoji: "❤️", label: "Love",  color: "#f33e58" },
  { emoji: "😂", label: "Haha",  color: "#f7b125" },
  { emoji: "😮", label: "Wow",   color: "#f7b125" },
  { emoji: "😢", label: "Sad",   color: "#f7b125" },
  { emoji: "😡", label: "Angry", color: "#e9710f" },
  { emoji: "👍", label: "Like",  color: "#0084ff" },
];

/* ─── Reaction Picker ─── */
function ReactionPicker({ onPick, onClose, isUser, currentReaction }) {
  return (
    <div
      onMouseLeave={onClose}
      style={{
        position: "absolute",
        bottom: "calc(100% + 8px)",
        [isUser ? "right" : "left"]: -8,
        background: "#fff",
        border: "1px solid rgba(0,0,0,0.1)",
        borderRadius: 50,
        padding: "6px 12px",
        display: "flex",
        gap: 2,
        boxShadow: "0 8px 30px rgba(0,0,0,0.18)",
        zIndex: 300,
        whiteSpace: "nowrap",
        animation: "um-popIn 0.22s cubic-bezier(.34,1.56,.64,1)",
      }}
    >
      {REACTIONS.map(({ emoji, label, color }, i) => {
        const isActive = currentReaction === emoji;
        return (
          <div key={emoji} style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <button
              onClick={() => { onPick(emoji); onClose(); }}
              title={label}
              style={{
                background: isActive ? `${color}22` : "none",
                border: "none",
                cursor: "pointer",
                fontSize: 26,
                padding: "4px",
                borderRadius: "50%",
                width: 46, height: 46,
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "transform 0.18s cubic-bezier(.34,1.56,.64,1)",
                transform: isActive ? "scale(1.25)" : "scale(1)",
                animationDelay: `${i * 25}ms`,
                lineHeight: 1,
                position: "relative",
                zIndex: 1,
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = "scale(1.55) translateY(-8px)";
                const tip = e.currentTarget.parentNode.querySelector(".um-reaction-tip");
                if (tip) { tip.style.opacity = "1"; tip.style.transform = "translateX(-50%) translateY(0) scale(1)"; }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = isActive ? "scale(1.25)" : "scale(1)";
                const tip = e.currentTarget.parentNode.querySelector(".um-reaction-tip");
                if (tip) { tip.style.opacity = "0"; tip.style.transform = "translateX(-50%) translateY(4px) scale(0.8)"; }
              }}
            >{emoji}</button>
            <span
              className="um-reaction-tip"
              style={{
                position: "absolute",
                bottom: -22,
                left: "50%",
                transform: "translateX(-50%) translateY(4px) scale(0.8)",
                background: "rgba(0,0,0,0.76)",
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                padding: "3px 8px",
                borderRadius: 8,
                whiteSpace: "nowrap",
                opacity: 0,
                transition: "all 0.14s ease",
                pointerEvents: "none",
                letterSpacing: "0.03em",
              }}
            >{label}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Reply Preview Bar ─── */
function ReplyBar({ replyTo, onCancel, isDark }) {
  if (!replyTo) return null;
  const isImg = replyTo.content?.startsWith("[IMAGE]");
  const preview = isImg ? "📷 Photo" : (replyTo.content?.slice(0, 60) + (replyTo.content?.length > 60 ? "…" : ""));
  return (
    <div style={{
      padding: "8px 14px",
      background: isDark ? "#1e2a3e" : "#f0f2ff",
      borderTop: `2px solid #0084ff`,
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexShrink: 0,
      animation: "um-slideUp 0.15s ease",
    }}>
      {/* Blue left bar */}
      <div style={{ width: 3, borderRadius: 3, alignSelf: "stretch", background: "#0084ff", flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#0084ff", marginBottom: 1 }}>
          Replying to {replyTo.senderType === "user" ? "yourself" : "Support"}
        </div>
        <div style={{
          fontSize: 12, color: isDark ? "#9ca3af" : "#65676b",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{preview}</div>
      </div>
      <button onClick={onCancel} style={{
        background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
        border: "none", borderRadius: "50%",
        width: 24, height: 24, cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: isDark ? "#9ca3af" : "#65676b",
        fontSize: 13, fontWeight: 900, flexShrink: 0,
        transition: "background 0.15s",
      }}>✕</button>
    </div>
  );
}

/* ─── Quoted Reply Snippet ─── */
function ReplySnippet({ quotedContent, quotedSenderType, isUser, isDark }) {
  const isImg = quotedContent?.startsWith("[IMAGE]");
  const text = isImg ? "📷 Photo" : (quotedContent?.slice(0, 80) + (quotedContent?.length > 80 ? "…" : ""));
  return (
    <div style={{
      borderLeft: `3px solid ${isUser ? "rgba(255,255,255,0.6)" : "#0084ff"}`,
      paddingLeft: 8,
      marginBottom: 5,
      borderRadius: "2px 4px 4px 2px",
      opacity: 0.85,
    }}>
      <div style={{
        fontSize: 10, fontWeight: 700,
        color: isUser ? "rgba(255,255,255,0.9)" : "#0084ff",
        marginBottom: 1,
      }}>
        {quotedSenderType === "user" ? "You" : "Support"}
      </div>
      <div style={{ fontSize: 11.5, color: isUser ? "rgba(255,255,255,0.8)" : (isDark ? "#9ca3af" : "#65676b"), lineHeight: 1.35 }}>
        {text}
      </div>
    </div>
  );
}

/* ─── Message Bubble ─── */
function MessageBubble({ msg, isUser, prevMsg, nextMsg, onReact, onReply, isDark }) {
  const [showPicker, setShowPicker] = useState(false);
  const [hovered, setHovered] = useState(false);
  const timerRef = useRef(null);
  const pickerTimerRef = useRef(null);

  const sameAuthorPrev = prevMsg && prevMsg.senderType === msg.senderType;
  const sameAuthorNext = nextMsg && nextMsg.senderType === msg.senderType;
  const showTime = !nextMsg || new Date(nextMsg.timestamp) - new Date(msg.timestamp) > 60000 * 5;

  const isImage = msg.content?.startsWith("[IMAGE]");
  const imageUrl = isImage ? msg.content.replace("[IMAGE]", "").trim() : null;

  // Parse reply info from content
  // Format: ↩ "quoted text" — actual message
  const isReply = msg.content?.startsWith("↩");
  let quotedText = null;
  let actualContent = msg.content;
  let quotedSenderType = null;

  if (isReply && !isImage) {
    const match = msg.content.match(/^↩ "(.*?)" — (.*)$/s);
    if (match) {
      quotedText = match[1];
      actualContent = match[2];
      // Heuristic: if user sent the reply, the quoted might be from admin, and vice versa
      quotedSenderType = isUser ? "admin" : "user";
    } else {
      actualContent = msg.content;
    }
  }

  const bubbleRadius = isUser
    ? `18px 4px ${sameAuthorNext ? "4px" : "18px"} 18px`
    : `4px 18px 18px ${sameAuthorNext ? "4px" : "18px"}`;

  const currentReaction = msg.reaction;

  const onEnter = () => {
    setHovered(true);
    clearTimeout(timerRef.current);
  };
  const onLeave = () => {
    setHovered(false);
    clearTimeout(timerRef.current);
    clearTimeout(pickerTimerRef.current);
    pickerTimerRef.current = setTimeout(() => setShowPicker(false), 300);
  };

  const bgColor = isUser ? "#0084ff" : (isDark ? "#2d2d4e" : "#f0f2f5");
  const textColor = isUser ? "#fff" : (isDark ? "#e0e0e0" : "#050505");

  return (
    <div style={{
      display: "flex",
      flexDirection: isUser ? "row-reverse" : "row",
      alignItems: "flex-end",
      gap: 6,
      marginTop: sameAuthorPrev ? 2 : 10,
      marginBottom: currentReaction ? 18 : 0,
    }}>
      {/* Admin avatar */}
      {!isUser && (
        <div style={{ width: 28, flexShrink: 0, alignSelf: "flex-end" }}>
          {!sameAuthorNext && (
            <div style={{
              width: 28, height: 28, borderRadius: "50%",
              background: "linear-gradient(135deg,#0084ff,#00c6ff)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 13, boxShadow: "0 2px 6px rgba(0,132,255,0.3)",
            }}>🎧</div>
          )}
        </div>
      )}

      <div style={{
        maxWidth: "68%",
        display: "flex", flexDirection: "column",
        alignItems: isUser ? "flex-end" : "flex-start",
      }}>
        {!isUser && !sameAuthorPrev && (
          <span style={{ fontSize: 10, color: isDark ? "#6b7280" : "#8a8d91", marginBottom: 2, marginLeft: 4 }}>
            Support Agent
          </span>
        )}

        {/* Bubble + Action buttons row */}
        <div
          style={{ display: "flex", flexDirection: isUser ? "row-reverse" : "row", alignItems: "center", gap: 4, position: "relative" }}
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
        >
          {/* Reaction Picker popup */}
          <div style={{ position: "relative" }}>
            {showPicker && (
              <ReactionPicker
                isUser={isUser}
                currentReaction={currentReaction}
                onPick={(emoji) => onReact(msg.messageId, emoji)}
                onClose={() => setShowPicker(false)}
              />
            )}
          </div>

          {/* Bubble */}
          <div style={{ position: "relative" }}>
            {isImage ? (
              <div
                style={{ borderRadius: 14, overflow: "hidden", maxWidth: 220, cursor: "pointer", boxShadow: "0 2px 12px rgba(0,0,0,0.15)" }}
                onClick={() => window.open(imageUrl, "_blank")}
              >
                <img src={imageUrl} alt="shared" style={{ width: "100%", display: "block" }} />
              </div>
            ) : (
              <div style={{
                background: bgColor,
                color: textColor,
                borderRadius: bubbleRadius,
                padding: quotedText ? "10px 13px 9px" : "9px 13px",
                fontSize: 14, lineHeight: 1.45,
                wordBreak: "break-word",
                boxShadow: isUser
                  ? "0 1px 4px rgba(0,132,255,0.25)"
                  : `0 1px 2px rgba(0,0,0,${isDark ? "0.3" : "0.08"})`,
                transition: "transform 0.1s",
              }}>
                {/* Quoted reply snippet */}
                {quotedText && (
                  <ReplySnippet
                    quotedContent={quotedText}
                    quotedSenderType={quotedSenderType}
                    isUser={isUser}
                    isDark={isDark}
                  />
                )}
                {actualContent}
              </div>
            )}

            {/* Reaction badge — Facebook style */}
            {currentReaction && (
              <div
                onClick={() => onReact(msg.messageId, currentReaction)}
                title="Click to remove"
                style={{
                  position: "absolute",
                  bottom: -14,
                  [isUser ? "left" : "right"]: 4,
                  background: isDark ? "#1e293b" : "#fff",
                  border: `1px solid ${isDark ? "#334155" : "#e4e6eb"}`,
                  borderRadius: 20,
                  padding: "2px 7px 2px 4px",
                  fontSize: 13,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.14)",
                  zIndex: 5,
                  cursor: "pointer",
                  userSelect: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  animation: "um-popIn 0.22s cubic-bezier(.34,1.56,.64,1)",
                  minWidth: 36,
                }}
              >
                <span style={{ fontSize: 14, lineHeight: 1 }}>{currentReaction}</span>
                <span style={{ fontSize: 11, color: isDark ? "#94a3b8" : "#65676b", fontWeight: 600 }}>1</span>
              </div>
            )}
          </div>

          {/* Hover action buttons */}
          {hovered && (
            <div style={{
              display: "flex",
              flexDirection: isUser ? "row-reverse" : "row",
              gap: 3,
              alignItems: "center",
              animation: "um-fadeIn 0.12s ease",
            }}>
              {/* React */}
              <button
                onClick={() => setShowPicker(v => !v)}
                title="React"
                style={{
                  background: isDark ? "#2d2d4e" : "#fff",
                  border: `1px solid ${isDark ? "#3d3d5e" : "#e4e6eb"}`,
                  borderRadius: "50%",
                  width: 30, height: 30,
                  cursor: "pointer",
                  fontSize: 15,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 1px 6px rgba(0,0,0,0.12)",
                  transition: "transform 0.1s, background 0.1s",
                  color: isDark ? "#9ca3af" : "#65676b",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.15)"; e.currentTarget.style.background = isDark ? "#3d3d5e" : "#f0f2f5"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = isDark ? "#2d2d4e" : "#fff"; }}
              >😊</button>

              {/* Reply */}
              <button
                onClick={() => onReply(msg)}
                title="Reply"
                style={{
                  background: isDark ? "#2d2d4e" : "#fff",
                  border: `1px solid ${isDark ? "#3d3d5e" : "#e4e6eb"}`,
                  borderRadius: "50%",
                  width: 30, height: 30,
                  cursor: "pointer",
                  fontSize: 14,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 1px 6px rgba(0,0,0,0.12)",
                  transition: "transform 0.1s, background 0.1s",
                  color: isDark ? "#9ca3af" : "#65676b",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.15)"; e.currentTarget.style.background = isDark ? "#3d3d5e" : "#f0f2f5"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = isDark ? "#2d2d4e" : "#fff"; }}
              >↩</button>
            </div>
          )}
        </div>

        {showTime && (
          <span style={{
            fontSize: 10, color: isDark ? "#4b5563" : "#8a8d91",
            marginTop: currentReaction ? 18 : 3,
            padding: "0 4px",
          }}>
            {formatTime(msg.timestamp)}
            {isUser && msg.read && " · Seen ✓"}
          </span>
        )}
      </div>
    </div>
  );
}

function ImagePreview({ file, onRemove }) {
  const [url] = useState(() => URL.createObjectURL(file));
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <img src={url} alt="preview" style={{
        height: 72, width: "auto", borderRadius: 10,
        border: "2px solid #0084ff", objectFit: "cover",
      }} />
      <button onClick={onRemove} style={{
        position: "absolute", top: -6, right: -6,
        background: "#e41e3f", color: "#fff",
        border: "2px solid #fff", borderRadius: "50%",
        width: 20, height: 20, cursor: "pointer",
        fontSize: 10, fontWeight: 900,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>✕</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// UserMessenger — Facebook Messenger-style chat widget
// Usage: <UserMessenger ref={messengerRef} userData={userData} isDarkMode={isDarkMode} />
// ─────────────────────────────────────────────────────────────────────────────
const UserMessenger = forwardRef(({ userData, isDarkMode }, ref) => {
  const username = userData?.username;

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [localReactions, setLocalReactions] = useState({});
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adminName, setAdminName] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [imageFile, setImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [replyTo, setReplyTo] = useState(null);

  const isDark = isDarkMode;

  const bottomRef = useRef(null);
  const pollRef = useRef(null);
  const fileRef = useRef(null);
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    openExpanded: () => {
      setOpen(true);
      setExpanded(true);
      markRead();
    },
  }));

  const windowStyle = expanded
    ? {
        position: "fixed",
        top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: "min(900px, 95vw)",
        height: "90vh",
        zIndex: 10002,
      }
    : {
        position: "absolute",
        bottom: 68, right: 0,
        width: 360, height: 530,
      };

  const bg        = isDark ? "#1a1a2e" : "#ffffff";
  const headerBg  = isDark ? "#16213e" : "#ffffff";
  const border    = isDark ? "#2d2d4e" : "#e4e6eb";
  const inputBg   = isDark ? "#0f3460" : "#f0f2f5";
  const textColor = isDark ? "#e0e0e0" : "#050505";
  const subText   = isDark ? "#9ca3af" : "#8a8d91";

  const fetchMessages = useCallback(async () => {
    if (!username) return;
    try {
      const res = await fetch(`${api}/Messages/user/${username}`);
      if (!res.ok) return;
      const data = await res.json();
      const sorted = [...data].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      setMessages(sorted);
      const adminMsg = sorted.find(m => m.admin);
      if (adminMsg) setAdminName(adminMsg.admin);
      if (!open) setUnreadCount(sorted.filter(m => m.senderType === "admin" && !m.read).length);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [username, open]);

  const markRead = useCallback(async () => {
    if (!username) return;
    try {
      await fetch(`${api}/Messages/markAllAsRead/${username}`, { method: "POST" });
      setUnreadCount(0);
    } catch {}
  }, [username]);

  useEffect(() => {
    fetchMessages();
    pollRef.current = setInterval(fetchMessages, 4000);
    return () => clearInterval(pollRef.current);
  }, [fetchMessages]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  // Focus input when replying
  useEffect(() => {
    if (replyTo) inputRef.current?.focus();
  }, [replyTo]);

  const openChat = () => { setOpen(true); markRead(); };
  const closeChat = () => { setOpen(false); setExpanded(false); setReplyTo(null); };

  const sendMessage = async (content) => {
    const text = content ?? input.trim();
    if (!text || sending) return;
    const targetAdmin = adminName ?? "admin";

    let finalContent = text;
    if (replyTo && !content) {
      const quotedPreview = replyTo.content?.startsWith("[IMAGE]")
        ? "📷 Photo"
        : replyTo.content?.slice(0, 60);
      finalContent = `↩ "${quotedPreview}" — ${text}`;
    }

    setSending(true);
    try {
      await fetch(`${api}/Messages/sendUser`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user: username, admin: targetAdmin, content: finalContent, senderType: "user" }),
      });
      if (!content) { setInput(""); setReplyTo(null); }
      await fetchMessages();
    } catch (e) { console.error(e); }
    setSending(false);
  };

  const sendImage = async () => {
    if (!imageFile) return;
    setUploadingImage(true);
    const targetAdmin = adminName ?? "admin";
    try {
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result);
        reader.readAsDataURL(imageFile);
      });
      const fd = new FormData();
      fd.append("file", imageFile);
      const uploadRes = await fetch(`${api}/Messages/uploadFile`, { method: "POST", body: fd });
      let fileName = imageFile.name;
      if (uploadRes.ok) { const uploadData = await uploadRes.json(); fileName = uploadData.fileName ?? imageFile.name; }
      const rawBase64 = base64.split(",")[1];
      await fetch(`${api}/Messages/sendUser`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user: username, admin: targetAdmin,
          content: `[IMAGE]${base64}`, senderType: "user",
          fileName, fileType: imageFile.type, fileData: rawBase64, fileSize: imageFile.size,
        }),
      });
      await fetchMessages();
    } catch (err) { console.error("❌ Image send error:", err); }
    finally { setImageFile(null); setUploadingImage(false); }
  };

  const handleReact = async (messageId, emoji) => {
    // Optimistic update
    setLocalReactions(prev => ({ ...prev, [messageId]: prev[messageId] === emoji ? null : emoji }));
    try {
      await fetch(`${api}/Messages/react/${messageId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reactionType: emoji }),
      });
    } catch (e) { console.error("React error:", e); }
  };

  const handleReply = (msg) => { setReplyTo(msg); };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (imageFile) sendImage(); else sendMessage();
    }
    if (e.key === "Escape") setReplyTo(null);
  };

  const mergedMessages = messages.map(m => ({
    ...m,
    // Priority: local optimistic → server reactionType → legacy likes fallback
    reaction: localReactions[m.messageId] !== undefined
      ? localReactions[m.messageId]
      : m.reactionType
        ? m.reactionType
        : m.likes ? "❤️" : null,
  }));

  const canSend = (input.trim() || imageFile) && !sending && !uploadingImage;

  return (
    <>
      <style>{`
        @keyframes um-popIn  { from{transform:scale(0.65);opacity:0} to{transform:scale(1);opacity:1} }
        @keyframes um-slideUp{ from{transform:translateY(14px);opacity:0} to{transform:translateY(0);opacity:1} }
        @keyframes um-fadeIn { from{opacity:0} to{opacity:1} }
        @keyframes um-spin   { to{transform:rotate(360deg)} }
        @keyframes um-pulse  {
          0%,100%{box-shadow:0 4px 20px rgba(0,132,255,0.45)}
          50%    {box-shadow:0 4px 32px rgba(0,132,255,0.85)}
        }
        .um-bubble:hover{transform:scale(1.1)!important}
        .um-messages::-webkit-scrollbar{width:4px}
        .um-messages::-webkit-scrollbar-track{background:transparent}
        .um-messages::-webkit-scrollbar-thumb{background:#c4c9d4;border-radius:4px}
      `}</style>

      {/* Backdrop for expanded mode */}
      {open && expanded && (
        <div
          onClick={closeChat}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 10001,
            animation: "um-fadeIn 0.2s ease",
          }}
        />
      )}

      {/* Fixed anchor */}
      <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 10002 }}>

        {/* Chat window */}
        {open && (
          <div style={{
            ...windowStyle,
            background: bg,
            borderRadius: expanded ? 20 : 18,
            border: `1px solid ${border}`,
            boxShadow: expanded ? "0 32px 100px rgba(0,0,0,0.4)" : "0 12px 48px rgba(0,0,0,0.22)",
            display: "flex", flexDirection: "column",
            overflow: "hidden",
            animation: "um-slideUp 0.2s ease",
            transition: "width 0.3s ease, height 0.3s ease, border-radius 0.3s ease",
          }}>

            {/* Header */}
            <div style={{
              padding: expanded ? "14px 20px" : "12px 14px",
              background: "linear-gradient(135deg,#0084ff 0%,#006fde 100%)",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              flexShrink: 0,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ position: "relative" }}>
                  <div style={{
                    width: expanded ? 48 : 40, height: expanded ? 48 : 40,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.2)",
                    border: "2px solid rgba(255,255,255,0.4)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: expanded ? 22 : 18, transition: "all 0.3s",
                  }}>🎧</div>
                  <span style={{
                    position: "absolute", bottom: 1, right: 1,
                    width: 11, height: 11, borderRadius: "50%",
                    background: "#31a24c", border: "2px solid #0084ff",
                  }} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: expanded ? 16 : 14, color: "#fff" }}>ORCA Support</div>
                  <div style={{ fontSize: expanded ? 12 : 11, color: "rgba(255,255,255,0.8)" }}>
                    {adminName ? `${adminName} is handling your chat` : "● Online · replies in minutes"}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  onClick={() => setExpanded(v => !v)}
                  title={expanded ? "Minimize" : "Expand"}
                  style={{
                    background: "rgba(255,255,255,0.18)", border: "none",
                    color: "#fff", borderRadius: "50%",
                    width: 32, height: 32, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.28)"}
                  onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}
                >
                  {expanded ? (
                    <svg width="16" height="16" fill="#fff" viewBox="0 0 24 24"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/></svg>
                  ) : (
                    <svg width="16" height="16" fill="#fff" viewBox="0 0 24 24"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>
                  )}
                </button>
                <button
                  onClick={closeChat}
                  title="Close"
                  style={{
                    background: "rgba(255,255,255,0.18)", border: "none",
                    color: "#fff", borderRadius: "50%",
                    width: 32, height: 32, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 15, transition: "background 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.28)"}
                  onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}
                >✕</button>
              </div>
            </div>

            {/* Messages */}
            <div
              className="um-messages"
              style={{
                flex: 1, overflowY: "auto",
                padding: expanded ? "16px 24px" : "12px 14px",
                background: bg, display: "flex", flexDirection: "column",
              }}
            >
              {loading ? (
                <div style={{ textAlign: "center", color: subText, paddingTop: 60, fontSize: 14 }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", border: "3px solid #ddd", borderTop: "3px solid #0084ff", animation: "um-spin 0.8s linear infinite", margin: "0 auto 10px" }} />
                  Loading...
                </div>
              ) : mergedMessages.length === 0 ? (
                <div style={{
                  flex: 1, display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  gap: 12, paddingBottom: 32,
                }}>
                  <div style={{
                    width: expanded ? 80 : 64, height: expanded ? 80 : 64,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg,#0084ff,#00c6ff)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: expanded ? 36 : 28,
                  }}>🎧</div>
                  <div style={{ fontWeight: 700, fontSize: expanded ? 20 : 16, color: textColor }}>ORCA Support</div>
                  <div style={{ fontSize: expanded ? 15 : 13, color: subText, textAlign: "center", lineHeight: 1.6, maxWidth: 300 }}>
                    Hi <strong style={{ color: textColor }}>{userData?.fullNames?.split(" ")[0] ?? username}</strong>! 👋
                    <br />How can we help you today?
                  </div>
                </div>
              ) : (
                <>
                  {mergedMessages[0] && (
                    <div style={{ textAlign: "center", marginBottom: 12 }}>
                      <span style={{
                        background: isDark ? "#2d2d4e" : "#e4e6eb",
                        color: subText, fontSize: 11, fontWeight: 600,
                        padding: "3px 12px", borderRadius: 20,
                      }}>{formatDay(mergedMessages[0].timestamp)}</span>
                    </div>
                  )}
                  {mergedMessages.map((msg, i) => (
                    <MessageBubble
                      key={msg.messageId ?? i}
                      msg={msg}
                      isUser={msg.senderType === "user"}
                      prevMsg={mergedMessages[i - 1]}
                      nextMsg={mergedMessages[i + 1]}
                      onReact={handleReact}
                      onReply={handleReply}
                      isDark={isDark}
                    />
                  ))}
                </>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Reply bar */}
            <ReplyBar replyTo={replyTo} onCancel={() => setReplyTo(null)} isDark={isDark} />

            {/* Image preview */}
            {imageFile && (
              <div style={{
                padding: "6px 16px 4px",
                borderTop: `1px solid ${border}`,
                background: bg, flexShrink: 0,
              }}>
                <ImagePreview file={imageFile} onRemove={() => setImageFile(null)} />
              </div>
            )}

            {/* Input bar */}
            <div style={{
              padding: expanded ? "10px 16px" : "8px 12px",
              borderTop: `1px solid ${border}`,
              background: headerBg,
              display: "flex", alignItems: "flex-end", gap: 8,
              flexShrink: 0,
            }}>
              <button
                onClick={() => fileRef.current?.click()}
                title="Send image"
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  flexShrink: 0, padding: "4px 2px",
                  display: "flex", alignItems: "center", borderRadius: "50%",
                  transition: "transform 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.transform = "scale(1.2)"}
                onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
              >
                <svg width="22" height="22" fill="none" stroke="#0084ff" viewBox="0 0 24 24" strokeWidth={1.8}>
                  <rect x="3" y="3" width="18" height="18" rx="3"/>
                  <circle cx="8.5" cy="8.5" r="1.5" fill="#0084ff" stroke="none"/>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 15l-5-5L5 21"/>
                </svg>
              </button>
              <input
                ref={fileRef} type="file" accept="image/*"
                style={{ display: "none" }}
                onChange={e => { const f = e.target.files[0]; if (f) setImageFile(f); e.target.value = ""; }}
              />

              <div style={{
                flex: 1, background: inputBg,
                borderRadius: 22, padding: "10px 16px",
              }}>
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={imageFile ? "Add a caption..." : replyTo ? "Write a reply..." : "Aa"}
                  rows={1}
                  style={{
                    width: "100%", border: "none", outline: "none",
                    background: "transparent", resize: "none",
                    fontSize: expanded ? 15 : 14, color: textColor,
                    lineHeight: 1.4, maxHeight: 120, overflow: "auto",
                    fontFamily: "system-ui, sans-serif",
                  }}
                />
              </div>

              {!canSend ? (
                <button
                  onClick={() => sendMessage("👍")}
                  title="Send 👍"
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: 24, color: "#0084ff", flexShrink: 0,
                    display: "flex", alignItems: "center",
                    transition: "transform 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = "scale(1.2)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                >👍</button>
              ) : (
                <button
                  onClick={imageFile ? sendImage : () => sendMessage()}
                  disabled={!canSend}
                  style={{
                    background: "#0084ff", border: "none",
                    borderRadius: "50%",
                    width: expanded ? 42 : 38, height: expanded ? 42 : 38,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", flexShrink: 0,
                    opacity: canSend ? 1 : 0.5,
                    boxShadow: "0 2px 8px rgba(0,132,255,0.35)",
                    transition: "transform 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = "scale(1.08)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                >
                  {uploadingImage || sending ? (
                    <div style={{
                      width: 16, height: 16, borderRadius: "50%",
                      border: "2px solid rgba(255,255,255,0.3)",
                      borderTop: "2px solid #fff",
                      animation: "um-spin 0.8s linear infinite",
                    }} />
                  ) : (
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="#fff"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Floating bubble */}
        <button
          onClick={open ? closeChat : openChat}
          className={!open && unreadCount > 0 ? "um-bubble" : ""}
          style={{
            width: 56, height: 56, borderRadius: "50%",
            background: "linear-gradient(135deg,#0084ff,#006fde)",
            border: "none", cursor: "pointer",
            boxShadow: "0 4px 20px rgba(0,132,255,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "transform 0.2s",
            position: "relative", zIndex: 10002,
          }}
          onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
          title={open ? "Close chat" : "Chat with Support"}
        >
          {open ? (
            <svg width="22" height="22" fill="#fff" viewBox="0 0 24 24">
              <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
            </svg>
          ) : (
            <svg width="26" height="26" fill="#fff" viewBox="0 0 24 24">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
            </svg>
          )}

          {!open && unreadCount > 0 && (
            <div style={{
              position: "absolute", top: -4, right: -4,
              background: "#e41e3f", color: "#fff",
              borderRadius: "50%", width: 20, height: 20,
              fontSize: 10, fontWeight: 800,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: "2px solid #fff",
              boxShadow: "0 2px 6px rgba(228,30,63,0.5)",
            }}>
              {unreadCount > 9 ? "9+" : unreadCount}
            </div>
          )}
        </button>
      </div>
    </>
  );
});

UserMessenger.displayName = "UserMessenger";
export default UserMessenger;